import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  format, 
  startOfMonth, 
  endOfMonth, 
  startOfWeek, 
  endOfWeek, 
  eachDayOfInterval, 
  isSameMonth, 
  isSameDay, 
  addMonths, 
  subMonths, 
  isToday 
} from 'date-fns';
import { motion, AnimatePresence } from 'framer-motion';
import { useVenues } from '../contexts/VenueContext';
import DashboardLayout from '../components/DashboardLayout';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiCalendar, FiChevronLeft, FiChevronRight, FiEdit3, FiDollarSign, FiSave, FiX, FiTrendingUp } = FiIcons;

const VenuePricingPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { getVenue, updateVenuePricing } = useVenues();
  const venue = getVenue(id);
  
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(null);
  const [newPrice, setNewPrice] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (!venue) navigate('/dashboard');
  }, [venue, navigate]);

  if (!venue) return null;

  const handleDateClick = (day) => {
    const dateStr = format(day, 'yyyy-MM-dd');
    setSelectedDate(day);
    setNewPrice(venue.priceOverrides[dateStr] || venue.pricePerNight);
  };

  const handleSavePrice = async (e) => {
    e.preventDefault();
    if (!selectedDate) return;
    setIsSaving(true);
    const dateStr = format(selectedDate, 'yyyy-MM-dd');
    await updateVenuePricing(venue.id, dateStr, newPrice);
    setIsSaving(false);
    setSelectedDate(null);
  };

  const renderMonthGrid = (monthDate) => {
    const days = eachDayOfInterval({
      start: startOfWeek(startOfMonth(monthDate)),
      end: endOfWeek(endOfMonth(monthDate))
    });

    return (
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden flex-1">
        <div className="p-3 bg-gray-50 border-b border-gray-100 text-center font-bold text-gray-700">
          {format(monthDate, 'MMMM yyyy')}
        </div>
        <div className="grid grid-cols-7 bg-gray-50 border-b border-gray-100">
          {['S', 'M', 'T', 'W', 'T', 'F', 'S'].map(d => (
            <div key={d} className="py-2 text-center text-[10px] font-bold text-gray-400 uppercase">{d}</div>
          ))}
        </div>
        <div className="grid grid-cols-7 gap-px bg-gray-200">
          {days.map((day, idx) => {
            const dateStr = format(day, 'yyyy-MM-dd');
            const overridePrice = venue.priceOverrides[dateStr];
            const isMonthMatch = isSameMonth(day, monthDate);
            const isSelected = selectedDate && isSameDay(day, selectedDate);

            return (
              <div 
                key={idx} 
                onClick={() => handleDateClick(day)}
                className={`min-h-[70px] p-1.5 cursor-pointer transition-all relative ${isMonthMatch ? 'bg-white' : 'bg-gray-50 text-gray-300'} ${isSelected ? 'ring-2 ring-blue-500 z-10' : 'hover:bg-blue-50/50'}`}
              >
                <div className="flex justify-between items-start">
                  <span className={`text-[10px] font-bold rounded-full h-5 w-5 flex items-center justify-center ${isToday(day) ? 'bg-blue-600 text-white' : 'text-gray-500'}`}>
                    {format(day, 'd')}
                  </span>
                  {overridePrice && <div className="w-1.5 h-1.5 rounded-full bg-indigo-500" />}
                </div>
                <div className="mt-2 text-center">
                  <p className={`text-xs font-bold ${overridePrice ? 'text-indigo-600' : 'text-gray-900'}`}>
                    €{overridePrice || venue.pricePerNight}
                  </p>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Dynamic Pricing</h1>
            <p className="text-gray-600">{venue.name}</p>
          </div>
          <div className="flex gap-2">
            <button onClick={() => setCurrentMonth(subMonths(currentMonth, 1))} className="p-2 bg-white border rounded-lg hover:bg-gray-50">
              <SafeIcon icon={FiChevronLeft} className="h-5 w-5" />
            </button>
            <button onClick={() => setCurrentMonth(addMonths(currentMonth, 1))} className="p-2 bg-white border rounded-lg hover:bg-gray-50">
              <SafeIcon icon={FiChevronRight} className="h-5 w-5" />
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 xl:grid-cols-4 gap-6">
          <div className="xl:col-span-3 grid grid-cols-1 lg:grid-cols-3 gap-4">
            {renderMonthGrid(currentMonth)}
            {renderMonthGrid(addMonths(currentMonth, 1))}
            {renderMonthGrid(addMonths(currentMonth, 2))}
          </div>

          <div className="space-y-4">
            <AnimatePresence mode="wait">
              {selectedDate ? (
                <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="bg-white p-6 rounded-xl border border-blue-200 shadow-sm">
                  <h3 className="font-bold text-gray-900 mb-4">Set Price</h3>
                  <div className="mb-4 text-sm font-medium text-blue-600 bg-blue-50 p-2 rounded">
                    {format(selectedDate, 'MMM d, yyyy')}
                  </div>
                  <form onSubmit={handleSavePrice} className="space-y-4">
                    <div className="relative">
                      <span className="absolute left-3 top-2.5 text-gray-400">€</span>
                      <input 
                        type="number" 
                        value={newPrice} 
                        onChange={(e) => setNewPrice(e.target.value)}
                        className="w-full pl-8 pr-4 py-2 border rounded-lg font-bold"
                      />
                    </div>
                    <button type="submit" disabled={isSaving} className="w-full bg-blue-600 text-white py-2 rounded-lg font-bold">
                      {isSaving ? 'Saving...' : 'Update'}
                    </button>
                    <button type="button" onClick={() => setSelectedDate(null)} className="w-full text-gray-500 text-sm">Cancel</button>
                  </form>
                </motion.div>
              ) : (
                <div className="bg-gray-50 p-6 rounded-xl border-2 border-dashed border-gray-200 text-center">
                  <SafeIcon icon={FiEdit3} className="h-8 w-8 text-gray-300 mx-auto mb-2" />
                  <p className="text-xs text-gray-500">Tap any date to set a custom price</p>
                </div>
              )}
            </AnimatePresence>
            
            <div className="bg-white p-6 rounded-xl border border-gray-200">
               <h4 className="font-bold text-gray-900 mb-4 flex items-center">
                 <SafeIcon icon={FiTrendingUp} className="mr-2 text-indigo-500" /> Insights
               </h4>
               <p className="text-xs text-gray-600 leading-relaxed">
                 Use the 3-month view to spot upcoming holidays and local events in <b>Iași</b>. Adjusting prices early ensures maximum revenue during peak seasons.
               </p>
            </div>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default VenuePricingPage;